import React, { useState, useEffect } from 'react';

interface PartnerCommercialSectionProps {
  activeTab: 'normal' | 'lsp' | 'lender';
  formData: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  initialData: any;
}

export const PartnerCommercialSection: React.FC<PartnerCommercialSectionProps> = ({ 
  activeTab, formData, handleChange, setFormData, initialData 
}) => {
  const [relationshipTypes, setRelationshipTypes] = useState<string[]>(['Lead Generation', 'Business Correspondent (BC)', 'Co-Lending']);
  const [isNewRelationship, setIsNewRelationship] = useState(false);
  const [newRelationshipName, setNewRelationshipName] = useState('');

  const [invoicingPeriods, setInvoicingPeriods] = useState<string[]>(['10th of every month', '15th of every month', '20th of every month']);
  const [isNewInvoicingPeriod, setIsNewInvoicingPeriod] = useState(false);
  const [newInvoicingPeriodName, setNewInvoicingPeriodName] = useState('');

  useEffect(() => {
    if (initialData) {
      if (initialData.relationship_type && !['Lead Generation', 'Business Correspondent (BC)', 'Co-Lending'].includes(initialData.relationship_type)) {
        setRelationshipTypes(prev => [...prev, initialData.relationship_type]);
      }
      if (initialData.invoicing_period && !['10th of every month', '15th of every month', '20th of every month'].includes(initialData.invoicing_period)) {
        setInvoicingPeriods(prev => [...prev, initialData.invoicing_period]);
      }
    }
  }, [initialData]);

  if (activeTab !== 'lsp' && activeTab !== 'lender') return null;

  return (
    <>
      <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '0.5rem', marginBottom: '1.5rem', marginTop: '2.5rem', color: 'var(--text-main)' }}>Partner Commercial Details</h3>
      <div className="form-row">
        <div className="filter-group">
          <label>Payout on Loan Amount (%)</label>
          <input type="number" step="0.01" name="payout_on_loan_amount" value={formData.payout_on_loan_amount} onChange={handleChange} placeholder="e.g. 1.5" />
        </div>
        <div className="filter-group">
          <label>Base Rate (%)</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input type="number" step="0.01" name="base_rate" value={formData.base_rate} onChange={handleChange} placeholder="e.g. 10.5" style={{ flex: 1 }} />
            <select name="base_rate_type" value={formData.base_rate_type} onChange={handleChange} style={{ width: 'auto' }}>
              <option value="inclusive">Inclusive</option>
              <option value="exclusive">Exclusive</option>
            </select>
          </div>
        </div>
      </div>
      <div className="form-row">
        <div className="filter-group">
          <label>Relationship Type</label>
          {!isNewRelationship ? (
            <select 
              name="relationship_type" 
              value={formData.relationship_type} 
              onChange={e => {
                if (e.target.value === 'NEW') {
                  setIsNewRelationship(true);
                } else {
                  handleChange(e);
                }
              }}
            >
              <option value="">Select...</option>
              {relationshipTypes.map(rt => (
                <option key={rt} value={rt}>{rt}</option>
              ))}
              <option value="NEW">+ Create New</option>
            </select>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
              <input 
                type="text" 
                placeholder="New Relationship Type" 
                required 
                value={newRelationshipName} 
                onChange={e => {
                  setNewRelationshipName(e.target.value);
                  setFormData((prev: any) => ({ ...prev, relationship_type: e.target.value }));
                }} 
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                <button 
                  type="button" 
                  onClick={() => {
                    setIsNewRelationship(false);
                    setNewRelationshipName('');
                    setFormData((prev: any) => ({ ...prev, relationship_type: initialData?.relationship_type || '' }));
                  }} 
                  style={{ fontSize: '0.85rem', color: 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!newRelationshipName.trim()) return;
                    if (!relationshipTypes.includes(newRelationshipName)) {
                      setRelationshipTypes(prev => [...prev, newRelationshipName]);
                    }
                    setIsNewRelationship(false);
                  }}
                  style={{ fontSize: '0.85rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="filter-group">
          <label>FLDG / Fixed Deposit (Cash Margin)</label>
          <input type="number" step="0.01" name="fldg" value={formData.fldg} onChange={handleChange} placeholder="Amount" />
        </div>
      </div>
      <div className="form-row">
        <div className="filter-group">
          <label>Processing Fee (%)</label>
          <input type="number" step="0.01" name="processing_fee_percent" value={formData.processing_fee_percent} onChange={handleChange} placeholder="e.g. 2.0" />
        </div>
        <div className="filter-group">
          <label>Processing Fee Share (%)</label>
          <input type="number" step="0.01" name="processing_fee_share" value={formData.processing_fee_share} onChange={handleChange} placeholder="e.g. 50" />
        </div>
      </div>
      <div className="form-row">
        <div className="filter-group">
          <label>Login Fees (Amount)</label>
          <input type="number" step="0.01" name="login_fees" value={formData.login_fees} onChange={handleChange} placeholder="e.g. 5000" />
        </div>
        <div className="filter-group">
          <label>HSN Code</label>
          <input type="text" name="hsn_code" value={formData.hsn_code} onChange={handleChange} placeholder="HSN Code" />
        </div>
      </div>
      <div className="form-row">
        <div className="filter-group">
          <label>Invoicing Period</label>
          {!isNewInvoicingPeriod ? (
            <select 
              name="invoicing_period" 
              value={formData.invoicing_period} 
              onChange={e => {
                if (e.target.value === 'NEW') {
                  setIsNewInvoicingPeriod(true);
                } else {
                  handleChange(e);
                }
              }}
            >
              <option value="">Select...</option>
              {invoicingPeriods.map(ip => (
                <option key={ip} value={ip}>{ip}</option>
              ))}
              <option value="NEW">+ Create New</option>
            </select>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
              <input 
                type="text" 
                placeholder="New Invoicing Period" 
                required 
                value={newInvoicingPeriodName} 
                onChange={e => {
                  setNewInvoicingPeriodName(e.target.value);
                  setFormData((prev: any) => ({ ...prev, invoicing_period: e.target.value }));
                }} 
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                <button 
                  type="button" 
                  onClick={() => {
                    setIsNewInvoicingPeriod(false);
                    setNewInvoicingPeriodName('');
                    setFormData((prev: any) => ({ ...prev, invoicing_period: initialData?.invoicing_period || '' }));
                  }} 
                  style={{ fontSize: '0.85rem', color: 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!newInvoicingPeriodName.trim()) return;
                    if (!invoicingPeriods.includes(newInvoicingPeriodName)) {
                      setInvoicingPeriods(prev => [...prev, newInvoicingPeriodName]);
                    }
                    setIsNewInvoicingPeriod(false);
                  }}
                  style={{ fontSize: '0.85rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {activeTab === 'lender' && (
        <>
          <div className="form-row" style={{ backgroundColor: '#f9fafb', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '1.5rem', marginTop: '1.5rem' }}>
            <div className="filter-group">
              <label>Base Hurdle Rate (%)</label>
              <input type="number" step="0.01" name="hurdle_rate_base" value={formData.hurdle_rate_base} onChange={handleChange} placeholder="e.g. 8.5" />
            </div>
            <div className="filter-group">
              <label>Cap Hurdle Rate (%)</label>
              <input type="number" step="0.01" name="hurdle_rate_cap" value={formData.hurdle_rate_cap} onChange={handleChange} placeholder="e.g. 12.0" />
            </div>
            <div className="filter-group">
              <label>Calculated ROI Share</label>
              <div style={{ padding: '0.5rem', backgroundColor: '#eef2ff', borderRadius: '4px', border: '1px solid #c7d2fe', color: '#3730a3', fontWeight: 600 }}>
                {formData.base_rate && formData.hurdle_rate_base 
                  ? `${(parseFloat(formData.base_rate) - parseFloat(formData.hurdle_rate_base)).toFixed(2)}%`
                  : 'Enter Base Rate & Hurdle Rate to calculate'}
              </div>
            </div>
          </div>
          <div className="form-row" style={{ backgroundColor: '#fff1f2', padding: '1rem', borderRadius: '8px', border: '1px solid #ffe4e6', marginBottom: '1.5rem' }}>
            <div className="filter-group" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: 0, fontWeight: 600 }}>
                <input type="checkbox" name="penal_charges_applicable" checked={formData.penal_charges_applicable} onChange={handleChange} style={{ width: 'auto' }} />
                Penal Charges Applicable
              </label>
            </div>
            {formData.penal_charges_applicable && (
              <div className="filter-group">
                <label>Penal Charges Amount</label>
                <input type="number" step="0.01" name="penal_charges_amount" value={formData.penal_charges_amount} onChange={handleChange} placeholder="Amount" />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};
